import { Divider, Dropdown, MenuProps } from 'antd';
import React, { FC, memo, ReactElement } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { RequestModalMode, requestFormModalActions } from 'widgets/RequestModal';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { LogOutButton } from './LogOutButton';
import { ReactComponent as SelectArrow } from 'shared/assets/icons/SelectArrow.svg';
import { ReactComponent as UserIcon } from 'shared/assets/icons/UserIcon.svg';
import { ReactComponent as AccountIcon } from 'shared/assets/icons/newIcons/AccountIcon.svg';
import { ReactComponent as MyBookingsIcon } from 'shared/assets/icons/MyBookingsIcon.svg';
import { ReactComponent as CallIcon } from 'shared/assets/icons/CallIcon.svg';
import { ReactComponent as BillsIcon } from 'shared/assets/icons/BillsIcon.svg';
import { ReactComponent as BookingIcon } from 'shared/assets/icons/newIcons/BookingIcon.svg';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

import { getLoggedUserData, useRole } from 'entities/User';
import { generateKnowledgeBaseUrl } from 'shared/utils/helpers/generateKnowledgeBaseUrl';

interface UserMenuProps {
  userEmail: string | undefined;
}

export const UserMenu: FC<UserMenuProps> = memo((props) => {
  const { userEmail } = props;

  const { t } = useAppTranslation(['header', 'booking', 'common', 'user']);

  const { isDesktop } = useScreenBreakpoints();

  const loggedUser = useAppSelector(getLoggedUserData);

  const currencySymbol = useGetCurrencySymbol();

  const { isManagement, isStaff, isPartner } = useRole();

  const globalSettings = useAppSelector(getGlobalSettings);

  const dispatch = useAppDispatch();

  const openRequestModal = (): void => {
    dispatch(
      requestFormModalActions.openRequestModal({
        title: t('Let`s get to know each other better!', { ns: 'booking' }),
        description: t('Please provide us with your information so that we can suggest the best pricing options that meet your needs.', {
          ns: 'booking',
        }),
        mode: RequestModalMode.CONTACT_US,
      }),
    );
  };

  let items: MenuProps['items'] = [
    {
      key: '0',
      label: (
        <Link to={`${AppRoutes.PROFILE}/${loggedUser?.userId || ''}`} className="flex items-center gap-2">
          <AccountIcon width={20} height={20} />
          {t('Profile')}
        </Link>
      ),
    },
    {
      key: '1',
      label: (
        <Link to={AppRoutes.MY_BOXES} className="flex items-center gap-2">
          <MyBookingsIcon width={18} height={18} />
          {t('My bookings', { ns: 'common' })}
        </Link>
      ),
    },
    {
      key: '2',
      label: (
        <div onClick={openRequestModal} className="flex items-center gap-2">
          <CallIcon width={20} height={20} />
          {t('Contact us')}
        </div>
      ),
    },
  ];

  if (isManagement) {
    const adminAdditionalRoute = {
      key: '3',
      label: (
        <Link to={AppRoutes.ADMIN_PANEL} className="flex items-center gap-2">
          <BillsIcon width={20} height={20} />
          {t('Admin panel', { ns: 'common' })}
        </Link>
      ),
    };

    items.push(adminAdditionalRoute);
  }

  if (isPartner && globalSettings?.partnerKnowledgeBaseUrl) {
    const adminAdditionalRoute = {
      key: '7',
      label: <Link to={generateKnowledgeBaseUrl(globalSettings.partnerKnowledgeBaseUrl)}>{t('Knowledge base')}</Link>,
      className: '!text-warn animate-pulse',
    };

    items.push(adminAdditionalRoute);
  }

  if (!isDesktop && !isManagement) {
    const customerAdditionalRoute = {
      key: '4',
      label: (
        <Link to={AppRoutes.BOOKING} className="flex items-center gap-2">
          <BookingIcon width={20} height={20} />
          {t('Booking')}
        </Link>
      ),
    };

    items.push(customerAdditionalRoute);
  }

  if (!isDesktop && !isManagement && isStaff) {
    const staffAdditionalRoutes = [
      {
        key: '5',
        label: <Link to={AppRoutes.WAREHOUSES}>{t('Warehouses')}</Link>,
      },
      {
        key: '6',
        label: <Link to={AppRoutes.BOXES}>{t('Boxes')}</Link>,
      },
    ];

    items = [...items, ...staffAdditionalRoutes];
  }

  const managementView = (): ReactElement => {
    return (
      <>
        <UserIcon className="[&>path]:stroke-accent" />
        <SelectArrow className="[&>path]:stroke-accent" />
      </>
    );
  };

  const customerView = (): ReactElement => {
    return (
      <div
        className={classNames('flex items-center gap-2 bg-black text-white rounded-lg', {
          'w-[40px] h-[40px] p-0 justify-center': !isDesktop,
          'p-2.5 pl-4 pr-3': isDesktop,
        })}
      >
        <AccountIcon className={classNames('desktop:hidden', { 'w-[25px] h-[25px]': !isDesktop, 'w-[22px] h-[22px]': isDesktop })} />
        <div className="hidden desktop:block">{t('My profile')}</div>
        <SelectArrow className="[&>path]:stroke-white [&>path]:stroke-[2] hidden desktop:block" />
      </div>
    );
  };

  return (
    <>
      {isDesktop ? (
        <Dropdown
          menu={{ items }}
          trigger={['click']}
          placement="bottom"
          dropdownRender={(menu) => (
            <div className="py-5 shadow-xl rounded-lg bg-white flex flex-col min-w-[220px]">
              <div className="py-2 text-primaryLight px-4">{userEmail}</div>
              <div className="flex justify-between px-4 text-primary">
                <span className="font-tektur font-semibold text-lg">{t('Balance', { ns: 'user' })}:</span>

                <span className="font-tektur font-semibold text-lg">
                  {formatPrice(loggedUser?.balance) || 0} {currencySymbol}
                </span>
              </div>
              {React.cloneElement(menu as ReactElement, {
                style: { boxShadow: 'none', background: 'none', display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 0px' },
              })}

              <LogOutButton theme="outlined" className="mt-4 px-4" />
            </div>
          )}
        >
          <div className="flex items-center text-accent cursor-pointer">{customerView()}</div>
        </Dropdown>
      ) : (
        <Link to={`${AppRoutes.PROFILE}/${loggedUser?.userId || ''}`} className="flex items-center gap-2">
          <div className="flex items-center text-accent cursor-pointer">{customerView()}</div>
        </Link>
      )}
    </>
  );
});
